// https://day.js.org/
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import updateLocale from "dayjs/plugin/updateLocale";

// ---- statically import the locales you support ----
import "dayjs/locale/en";     // base en (also for :us)
import "dayjs/locale/en-gb";  // "en-GB"
import "dayjs/locale/de";
import "dayjs/locale/de-at";  // :at
import "dayjs/locale/ru";
import "dayjs/locale/uk";     // :ua
import "dayjs/locale/hi";     // :in (Hindi)
import "dayjs/locale/zh-cn";  // :cn
import "dayjs/locale/es";     // :es and :co
import "dayjs/locale/fr";
import "dayjs/locale/it";
import "dayjs/locale/pt";     // :pt
import "dayjs/locale/pt-br";  // :br
import "dayjs/locale/el";     // :gr
import "dayjs/locale/ro";
import "dayjs/locale/tr";
import "dayjs/locale/vi";
import "dayjs/locale/id";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(updateLocale);

// Normalize your app’s locales to Day.js locale codes
const normalize = (loc) => {
  const l = (loc || "en").toString().toLowerCase();
  switch (l) {
    case "en-gb": return "en-gb";
    case "us":    return "en";     // map :us → en
    case "co":    return "es";     // map :co (Colombia) → es
    case "at":    return "de-at";
    case "gr":    return "el";
    case "cn":    return "zh-cn";
    case "br":    return "pt-br";
    case "ua":    return "uk";
    case "in":    return "hi";
    // supported directly:
    case "ru":
    case "en":
    case "de":
    case "es":
    case "it":
    case "fr":
    case "pt":
    case "ro":
    case "tr":
    case "vi":
    case "id":
    case "uk":
    case "hi":
    case "zh-cn":
    case "el":
    case "pt-br":
    case "de-at":
      return l;
    default:
      return "en";
  }
};

const appLocale = normalize(window.locale);

// Helpers to format relative time in different locales
const ssByLocale = {
  en: "%d sec",
  "en-gb": "%d sec",
  de: "%d Sek.",
  "de-at": "%d Sek.",
  ru: "%d сек",
  uk: "%d сек",
  hi: "%d सेकंड",
  "zh-cn": "%d 秒",
  es: "%d seg",
  fr: "%d sec",
  it: "%d sec",
  pt: "%d seg",
  "pt-br": "%d seg",
  el: "%d δευτ.",
  ro: "%d sec",
  tr: "%d sn",
  vi: "%d giây",
  id: "%d detik",
};

// full-word singulars per locale
const mByLocale = {
  en: "1 minute",
  "en-gb": "1 minute",
  de: "1 Minute",
  "de-at": "1 Minute",
  ru: "1 минута",
  uk: "1 хвилина",
  hi: "1 मिनट",
  "zh-cn": "1 分钟",
  es: "1 minuto",
  fr: "1 minute",
  it: "1 minuto",
  pt: "1 minuto",
  "pt-br": "1 minuto",
  el: "1 λεπτό",
  ro: "1 minut",
  tr: "1 dakika",
  vi: "1 phút",
  id: "1 menit",
};

const hByLocale = {
  en: "1 hour",
  "en-gb": "1 hour",
  de: "1 Stunde",
  "de-at": "1 Stunde",
  ru: "1 час",
  uk: "1 година",
  hi: "1 घंटा",
  "zh-cn": "1 小时",
  es: "1 hora",
  fr: "1 heure",
  it: "1 ora",
  pt: "1 hora",
  "pt-br": "1 hora",
  el: "1 ώρα",
  ro: "1 oră",
  tr: "1 saat",
  vi: "1 giờ",
  id: "1 jam",
};

const dByLocale = {
  en: "1 day",
  "en-gb": "1 day",
  de: "1 Tag",
  "de-at": "1 Tag",
  ru: "1 день",
  uk: "1 день",
  hi: "1 दिन",
  "zh-cn": "1 天",
  es: "1 día",
  fr: "1 jour",
  it: "1 giorno",
  pt: "1 dia",
  "pt-br": "1 dia",
  el: "1 μέρα",
  ro: "1 zi",
  tr: "1 gün",
  vi: "1 ngày",
  id: "1 hari",
};

// Apply locale and patch relativeTime
dayjs.locale(appLocale);

// helper: force "1 unit" style across locales
const forceNumericSingular = (rt, localeKey) => ({
  ...rt,
  s: ssByLocale[localeKey] || rt.s || "%d sec",
  ss: ssByLocale[localeKey] || rt.ss || "%d sec",
  m: mByLocale[localeKey] || rt.m || "1 min",
  h: hByLocale[localeKey] || rt.h || "1 h",
  d: dByLocale[localeKey] || rt.d || "%d d",
  M: rt.MM,
  y: rt.yy,
});

if (appLocale === "en" || appLocale === "en-gb") {
  dayjs.updateLocale(appLocale, {
    relativeTime: forceNumericSingular(
      {
        future: "in %s",
        past: "%s ago",
        s: ssByLocale[appLocale],
        ss: ssByLocale[appLocale],
        mm: "%d minutes",
        hh: "%d hours",
        dd: "%d days",
        MM: "%d month",
        yy: "%d years",
      },
      appLocale
    ),
  });
} else {
  const base = dayjs.Ls[appLocale] || {};
  const baseRT = base.relativeTime || {};
  console.log(baseRT);
  dayjs.updateLocale(appLocale, {
    relativeTime: forceNumericSingular(baseRT, appLocale),
  });
}

// Usage:
// dayjs(someDate).fromNow(true)  // will show "20 sec", "20 сек", "20 秒", etc.
